import {
  CHANNEL_KINDS,
  CONVERSATION_STATUSES,
  META_SESSION_TYPES,
  OPERATION_MODE_KINDS,
  type ChannelKind,
  type ConversationStatus,
  type MetaSessionType,
  type OperationModeKind,
} from '@lume/shared';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { InboxFilters, InboxSortOption, InboxTab } from '../features/inbox/types';

const STATUSES = new Set<string>(CONVERSATION_STATUSES);
const CHANNELS = new Set<string>(CHANNEL_KINDS);
const MODES = new Set<string>(OPERATION_MODE_KINDS);
const TABS = new Set<string>(['awaiting-me', 'awaiting-anyone', 'team-queue', 'ai-driving', 'all']);
const SORTS = new Set<string>(['urgency', 'recency', 'aging']);
const AWAITING = new Set<string>(['customer', 'operator', 'system']);
const META_SESSIONS = new Set<string>(META_SESSION_TYPES);

/**
 * `useInboxFilters` — mantém os filtros da Inbox completa em URL search params.
 *
 * Extensão de `useConversationFilters` com suporte a filtros premium da Inbox:
 *   - awaitingParty, intentFilter, commitmentFilter, sequenceFlag, inGroup
 *
 * Filtros premium são passados via URL sem validação de capability — a UI
 * aplica CapabilityGate separadamente. O servidor valida permissão quando
 * processar a query.
 *
 * `defaultSort` (Alvo B round2): o default de ordenação é do CALLER (a página
 * resolve via capability `inbox-recency-default`). `?sort=` explícito na URL
 * sempre vence; o param só é escrito quando diverge do default.
 */
export function useInboxFilters(options?: { defaultSort?: InboxSortOption }): {
  filters: InboxFilters;
  setFilter: <K extends keyof InboxFilters>(key: K, value: InboxFilters[K] | undefined) => void;
  clearFilters: () => void;
  setTab: (tab: InboxTab) => void;
  currentTab: InboxTab;
  sortOption: InboxSortOption;
  setSortOption: (opt: InboxSortOption) => void;
} {
  const [params, setParams] = useSearchParams();
  const defaultSort: InboxSortOption = options?.defaultSort ?? 'urgency';

  const parsed: InboxFilters = {};

  const rawStatus = params.get('status');
  if (rawStatus && STATUSES.has(rawStatus)) parsed.status = rawStatus as ConversationStatus;

  const rawChannel = params.get('channel');
  if (rawChannel && CHANNELS.has(rawChannel)) parsed.channel = rawChannel as ChannelKind;

  const rawMode = params.get('operationMode');
  if (rawMode && MODES.has(rawMode)) parsed.operationMode = rawMode as OperationModeKind;

  const rawAssigned = params.get('assignedToUserId');
  if (rawAssigned) parsed.assignedToUserId = rawAssigned;

  const rawSearch = params.get('search');
  if (rawSearch) parsed.search = rawSearch;

  const rawAwaiting = params.get('awaitingParty');
  if (rawAwaiting && AWAITING.has(rawAwaiting)) {
    parsed.awaitingParty = rawAwaiting as NonNullable<InboxFilters['awaitingParty']>;
  }

  const rawIntent = params.get('intentFilter');
  if (rawIntent) parsed.intentFilter = rawIntent;

  const rawCommitment = params.get('commitmentFilter');
  if (rawCommitment)
    parsed.commitmentFilter = rawCommitment as NonNullable<InboxFilters['commitmentFilter']>;

  const rawSeqFlag = params.get('sequenceFlag');
  if (rawSeqFlag) parsed.sequenceFlag = rawSeqFlag as NonNullable<InboxFilters['sequenceFlag']>;

  if (params.get('inGroup') === 'true') parsed.inGroup = true;

  const rawMeta = params.get('metaSessionType');
  if (rawMeta && META_SESSIONS.has(rawMeta)) parsed.metaSessionType = rawMeta as MetaSessionType;

  const rawTab = params.get('tab') ?? 'awaiting-me';
  const currentTab: InboxTab = TABS.has(rawTab) ? (rawTab as InboxTab) : 'awaiting-me';

  const rawSort = params.get('sort') ?? defaultSort;
  const sortOption: InboxSortOption = SORTS.has(rawSort)
    ? (rawSort as InboxSortOption)
    : defaultSort;

  const setFilter = useCallback<
    <K extends keyof InboxFilters>(key: K, value: InboxFilters[K] | undefined) => void
  >(
    (key, value) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === undefined || value === '' || value === null) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams();
        const keepTab = current.get('tab');
        const keepSort = current.get('sort');
        if (keepTab) next.set('tab', keepTab);
        if (keepSort) next.set('sort', keepSort);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const setTab = useCallback(
    (next: InboxTab) => {
      setParams(
        (current) => {
          const p = new URLSearchParams(current);
          if (next === 'awaiting-me') {
            p.delete('tab');
          } else {
            p.set('tab', next);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setSortOption = useCallback(
    (next: InboxSortOption) => {
      setParams(
        (current) => {
          const p = new URLSearchParams(current);
          if (next === defaultSort) {
            p.delete('sort');
          } else {
            p.set('sort', next);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams, defaultSort],
  );

  return {
    filters: parsed,
    setFilter,
    clearFilters,
    setTab,
    currentTab,
    sortOption,
    setSortOption,
  };
}
